import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { User, Pencil, Check, X, Loader2 } from 'lucide-react';
import { getTierForPoints } from '@/lib/loyaltyTiers';
import { inputClass } from './caseConstants';

// Who we're dealing with, plus the numbers that decide how much latitude to
// give them: how often they've been in, what they've spent, and their tier.
export default function CaseCustomerCard({ caseData, customer, onDone }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    customer_name: caseData.customer_name || '',
    customer_email: caseData.customer_email || '',
    customer_phone: caseData.customer_phone || '',
  });

  const tier = getTierForPoints(customer?.lifetime_points || 0);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await base44.functions.invoke('caseUpdate', {
        caseId: caseData.id,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
      });
      if (res.data?.error) throw new Error(res.data.error);
      setEditing(false);
      if (onDone) onDone();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not save.');
    }
    setSaving(false);
  };

  return (
    <div className="border border-white/5 bg-[#111115]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <User className="w-3.5 h-3.5 text-primary" />
          <p className="text-white text-[10px] font-bold tracking-widest">CUSTOMER</p>
        </div>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="text-white/30 hover:text-primary transition-colors"
            aria-label="Edit customer details"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(false)} className="text-white/30 hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
            <button onClick={save} disabled={saving} className="text-primary hover:text-primary/80 transition-colors">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}
      </div>

      <div className="px-4 py-3 space-y-3">
        {editing ? (
          <>
            <input
              value={form.customer_name}
              onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))}
              placeholder="Full name"
              className={inputClass}
            />
            <input
              value={form.customer_email}
              onChange={(e) => setForm((f) => ({ ...f, customer_email: e.target.value }))}
              placeholder="Email address"
              className={inputClass}
            />
            <input
              value={form.customer_phone}
              onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))}
              placeholder="Phone number"
              className={inputClass}
            />
          </>
        ) : (
          <>
            <p className="text-white font-bold text-sm truncate">
              {caseData.customer_name || 'Unnamed contact'}
            </p>
            {caseData.customer_email ? (
              <a
                href={`mailto:${caseData.customer_email}`}
                className="block text-white/50 hover:text-primary text-xs truncate transition-colors"
              >
                {caseData.customer_email}
              </a>
            ) : (
              <p className="text-white/25 text-xs">No email on record</p>
            )}
            {caseData.customer_phone ? (
              <a
                href={`tel:${caseData.customer_phone}`}
                className="block text-white/50 hover:text-primary text-xs transition-colors"
              >
                {caseData.customer_phone}
              </a>
            ) : (
              <p className="text-white/25 text-xs">No phone on record</p>
            )}
          </>
        )}

        {error && <p className="text-red-400 text-[11px]">{error}</p>}

        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/5">
          <div>
            <p className="text-white font-black text-lg">{customer?.booking_count ?? 0}</p>
            <p className="text-white/30 text-[10px] font-bold tracking-wider">LIFETIME BOOKINGS</p>
          </div>
          <div>
            <p className="text-white font-black text-lg">£{Number(customer?.total_spend || 0).toFixed(2)}</p>
            <p className="text-white/30 text-[10px] font-bold tracking-wider">TOTAL SPEND</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 pt-1">
          <span
            className="text-[10px] font-bold tracking-widest px-2.5 py-1 border"
            style={{ color: tier.color, borderColor: `${tier.color}55`, background: `${tier.color}14` }}
          >
            {tier.name.toUpperCase()}
          </span>
          <span className="text-white/30 text-[10px]">{customer?.lifetime_points ?? 0} pts earned</span>
        </div>
      </div>
    </div>
  );
}